import { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import CollapsibleSection from './CollapsibleSection';
import TagInput from './TagInput';
import { ConfigItemList } from './ConfigItemList';
import { FrontmatterEditorFields } from './FrontmatterEditorFields';
import { useFrontmatterEditor } from './useFrontmatterEditor';
import { docsApi } from '@/services/docsApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface SkillMeta {
  path: string;
  name: string;
}

export default function SkillsSection() {
  const { selectedRepo } = useWorkspace();
  const [skills, setSkills] = useState<SkillMeta[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { attrs, body, updateAttr, updateBody, resetContent, clear, config } =
    useFrontmatterEditor(selectedSkill || '__none__');

  useEffect(() => {
    if (!selectedRepo) {
      setLoading(false);
      return;
    }
    const [owner, repo] = selectedRepo.split('/');
    docsApi
      .getRepoTree(owner, repo)
      .then((tree) => {
        const found = findSkills(tree);
        setSkills(found);
      })
      .catch(() => setSkills([]))
      .finally(() => setLoading(false));
  }, [selectedRepo]);

  function findSkills(node: { name: string; path: string; type: string; children: typeof node[] }): SkillMeta[] {
    const results: SkillMeta[] = [];
    if (node.name === 'SKILL.md' && node.path.includes('.claude/skills/')) {
      const parts = node.path.split('/');
      const nameIdx = parts.indexOf('skills') + 1;
      results.push({ path: node.path, name: parts[nameIdx] || node.path });
    }
    for (const child of node.children || []) {
      results.push(...findSkills(child));
    }
    return results;
  }

  function handleCreate(name: string) {
    const safeName = name.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    const path = `.claude/skills/${safeName}/SKILL.md`;
    setSkills([...skills, { path, name: safeName }]);
    setSelectedSkill(path);
    resetContent(
      { name: safeName, description: '', 'user-invocable': true },
      '\nYour skill instructions here.\n',
    );
  }

  function handleDelete() {
    if (!selectedSkill) return;
    setSkills(skills.filter((s) => s.path !== selectedSkill));
    setSelectedSkill(null);
    clear();
  }

  return (
    <CollapsibleSection
      title="Skills"
      description="Custom slash commands for Claude Code"
      filePath=".claude/skills/"
      icon={Zap}
      exists={skills.length > 0}
      loading={loading}
    >
      {loading ? (
        <LoadingState size="md" />
      ) : (
        <div className="flex gap-4">
          <ConfigItemList
            items={skills}
            selectedPath={selectedSkill}
            onSelect={setSelectedSkill}
            onCreate={handleCreate}
            placeholder="skill-name"
            newLabel="New skill"
          />

          <FrontmatterEditorFields
            attrs={attrs}
            body={body}
            updateAttr={updateAttr}
            updateBody={updateBody}
            loading={config.loading}
            saving={config.saving}
            saved={config.saved}
            onSave={() => config.save(`chore: update skill ${attrs.name || selectedSkill} (via SystemDox)`)}
            onDelete={handleDelete}
            isSelected={!!selectedSkill}
            hasItems={skills.length > 0}
            emptyMessage="Create your first skill"
            extraFields={
              <>
                <div className="flex items-end gap-2 pb-1">
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={attrs['user-invocable'] !== false}
                      onChange={(e) => updateAttr('user-invocable', e.target.checked)}
                      className="rounded"
                    />
                    User-invocable
                  </label>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Allowed tools</label>
                  <TagInput
                    tags={((attrs['allowed-tools'] as string) || '').split(',').map((s) => s.trim()).filter(Boolean)}
                    onChange={(tags) => updateAttr('allowed-tools', tags.join(', '))}
                    placeholder="Bash, Read, Grep"
                  />
                </div>
              </>
            }
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
